import { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { aiApi } from '../../api/ai';
import type { CampaignDesignResponse, LureResponse } from '../../api/ai';

interface AiCampaignDesignerProps {
  onApplyName?: (name: string) => void;
  onApplyTemplate?: (subject: string, html: string, text: string) => void | Promise<void>;
}

export function AiCampaignDesigner({ onApplyName, onApplyTemplate }: AiCampaignDesignerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [objective, setObjective] = useState('');
  const [audience, setAudience] = useState('');
  const [difficulty, setDifficulty] = useState('intermediate');
  const [industry, setIndustry] = useState('');
  const [attackVector, setAttackVector] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [design, setDesign] = useState<CampaignDesignResponse | null>(null);
  const [lurePrompt, setLurePrompt] = useState('');
  const [lureBrand, setLureBrand] = useState('');
  const [lureScenario, setLureScenario] = useState('');
  const [lureTone, setLureTone] = useState('professional');
  const [lure, setLure] = useState<LureResponse | null>(null);
  const [lureApplied, setLureApplied] = useState(false);
  const [applyingLure, setApplyingLure] = useState(false);

  const handleGenerate = async () => {
    if (!objective && !audience) {
      setError('Enter at least an objective or audience description.');
      return;
    }
    setLoading(true);
    setError('');
    setDesign(null);
    try {
      const res = await aiApi.designCampaign({
        objective,
        audience,
        difficulty,
        industry,
        attack_vector: attackVector,
      });
      setDesign(res.data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || 'Failed to generate campaign design. Check your AI settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLure = async () => {
    if (!lurePrompt.trim()) { setError('Describe the authorized simulation lure to generate.'); return; }
    setLoading(true); setError(''); setLure(null); setLureApplied(false);
    try {
      const response = await aiApi.generateLure({ prompt: lurePrompt.trim(), brand: lureBrand.trim(), scenario: lureScenario.trim(), tone: lureTone });
      setLure(response.data);
    } catch (err: unknown) {
      const failure = err as { response?: { data?: { message?: string } } };
      setError(failure.response?.data?.message || 'Failed to generate a lure. Check AI settings and try again.');
    } finally { setLoading(false); }
  };

  const applyLure = async () => {
    if (!lure || !onApplyTemplate) return;
    setApplyingLure(true);
    setError('');
    try {
      await onApplyTemplate(lure.subject, lure.html, lure.text);
      setLureApplied(true);
    } catch {
      setLureApplied(false);
      setError('Could not use this lure in the campaign. Try again.');
    } finally {
      setApplyingLure(false);
    }
  };

  return (
    <Card>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-50 rounded-lg">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">AI Campaign Designer</h3>
            <p className="text-sm text-gray-500">Generate campaign content with AI</p>
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>

      {isOpen && (
        <div className="mt-4 space-y-4">
          <section className="rounded-lg border border-purple-100 bg-purple-50/40 p-4 space-y-3" aria-label="AI lure generator">
            <div><h4 className="font-semibold text-gray-900">Generate a simulation lure</h4><p className="text-sm text-gray-600">Drafts are not saved automatically. Review the content before using it in a campaign.</p></div>
            <label className="label">Lure instructions<textarea className="input min-h-20" value={lurePrompt} onChange={event => setLurePrompt(event.target.value)} placeholder="Describe the message and learning objective" /></label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="label">Brand<input className="input" value={lureBrand} onChange={event => setLureBrand(event.target.value)} /></label>
              <label className="label">Scenario<input className="input" value={lureScenario} onChange={event => setLureScenario(event.target.value)} /></label>
              <label className="label">Tone<select className="input" value={lureTone} onChange={event => setLureTone(event.target.value)}><option>professional</option><option>urgent</option><option>friendly</option><option>formal</option></select></label>
            </div>
            <Button onClick={() => void handleGenerateLure()} loading={loading} disabled={!lurePrompt.trim()}>Generate lure</Button>
            {lure && <div className="space-y-3 rounded-lg border bg-white p-4"><h5 className="font-medium">{lure.subject}</h5><details><summary className="cursor-pointer text-sm font-medium">HTML draft</summary><pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-3 text-xs">{lure.html}</pre></details><details><summary className="cursor-pointer text-sm font-medium">Plain text draft</summary><pre className="mt-2 whitespace-pre-wrap break-words rounded bg-gray-50 p-3 text-xs">{lure.text}</pre></details>{onApplyTemplate && <Button variant="secondary" loading={applyingLure} onClick={() => void applyLure()}>{lureApplied ? 'Lure selected' : 'Use this lure in the campaign'}</Button>}{lureApplied && <p className="text-sm text-gray-600">This lure is selected as the email template.</p>}</div>}
          </section>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Campaign Objective</label>
              <input
                type="text"
                className="input"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                placeholder="Test employee awareness of credential harvesting"
              />
            </div>
            <div>
              <label className="label">Target Audience</label>
              <input
                type="text"
                className="input"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="Finance department employees"
              />
            </div>
            <div>
              <label className="label">Difficulty</label>
              <select
                className="input"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <option value="introductory">Introductory</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="label">Industry</label>
              <input
                type="text"
                className="input"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Financial services, Healthcare, etc."
              />
            </div>
            <div className="md:col-span-2">
              <label className="label">Attack Vector</label>
              <input
                type="text"
                className="input"
                value={attackVector}
                onChange={(e) => setAttackVector(e.target.value)}
                placeholder="Credential harvesting, invoice fraud, package delivery, etc."
              />
            </div>
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600">{error}</p>
          )}

          <Button onClick={handleGenerate} loading={loading}>
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Campaign Design
          </Button>

          {design && (
            <div className="space-y-4 mt-4 pt-4 border-t border-gray-200">
              <h4 className="font-semibold text-gray-900">Generated Design</h4>

              {design.campaign_name && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-700">Campaign Name</p>
                    {onApplyName && (
                      <button
                        type="button"
                        onClick={() => onApplyName(design.campaign_name)}
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        Apply
                      </button>
                    )}
                  </div>
                  <p className="text-sm">{design.campaign_name}</p>
                </div>
              )}

              {design.subject && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-700">Email Subject</p>
                    {onApplyTemplate && (
                      <button
                        type="button"
                        onClick={() => onApplyTemplate(design.subject, design.email_html || '', design.email_text || '')}
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        Create as Template
                      </button>
                    )}
                  </div>
                  <p className="text-sm">{design.subject}</p>
                </div>
              )}

              {design.rationale && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-700 mb-1">Design Rationale</p>
                  <p className="text-sm text-blue-800">{design.rationale}</p>
                </div>
              )}

              {design.schedule && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-1">Recommended Schedule</p>
                  <p className="text-sm">{design.schedule}</p>
                </div>
              )}

              {design.difficulty && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-1">Difficulty Assessment</p>
                  <p className="text-sm capitalize">{design.difficulty}</p>
                </div>
              )}

              {design.email_html && (
                <details className="p-3 bg-gray-50 rounded-lg">
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">Email HTML Preview</summary>
                  <pre className="text-xs mt-2 overflow-x-auto whitespace-pre-wrap break-words">{design.email_html}</pre>
                </details>
              )}

              {design.landing_html && (
                <details className="p-3 bg-gray-50 rounded-lg">
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">Landing Page HTML Preview</summary>
                  <pre className="text-xs mt-2 overflow-x-auto whitespace-pre-wrap break-words">{design.landing_html}</pre>
                </details>
              )}

              {design.raw && (
                <details className="p-3 bg-yellow-50 rounded-lg">
                  <summary className="text-sm font-medium text-yellow-700 cursor-pointer">Raw AI Response</summary>
                  <pre className="text-xs mt-2 overflow-x-auto whitespace-pre-wrap break-words">{design.raw}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
